import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { SocialService } from '@delon/auth';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzI18nModule } from 'ng-zorro-antd/i18n';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { TenantStateService } from 'src/app/services/tenant-state.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

type DashboardStatisticKey =
  | 'employeeCount'
  | 'contactCount'
  | 'bossServiceNumberCount'
  | 'serviceNumberCount'
  | 'aiffCount'
  | 'onlineUserCount'
  | 'thirtyDayActiveUserCount';

interface DashboardStatistics {
  aiffCount: number;
  employeeCount: number;
  serviceNumberCount: number;
  bossServiceNumberCount: number;
  contactCount: number;
  onlineUserCount: number;
  thirtyDayActiveUserCount: number;
}

interface DashboardMetricCard {
  key: DashboardStatisticKey;
  titleKey: string;
  icon: string;
  iconClass: string;
  testIdSuffix: string;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.less'],
  providers: [SocialService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NzButtonModule,
    NzTableModule,
    NzI18nModule,
    NzSpinModule,
    NzBreadCrumbModule,
    NzCardModule,
    NzIconModule,
    NzGridModule,
    I18nPipe,
    TestIdDirective
  ]
})
export class DashboardComponent implements OnInit {
  private readonly http = inject(HttpService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly tenantStateService = inject(TenantStateService);
  private readonly i18n = inject(ALAIN_I18N_TOKEN);

  loading = true;
  hasError = false;
  currentTenantId = '';
  readonly metricCards: DashboardMetricCard[] = [
    { key: 'employeeCount', titleKey: 'dashboard.employee-count', icon: 'team', iconClass: 'employee-icon', testIdSuffix: 'employee' },
    { key: 'contactCount', titleKey: 'dashboard.customer-count', icon: 'user', iconClass: 'customer-icon', testIdSuffix: 'customer' },
    {
      key: 'bossServiceNumberCount',
      titleKey: 'dashboard.business-number-count',
      icon: 'shop',
      iconClass: 'business-icon',
      testIdSuffix: 'business'
    },
    {
      key: 'serviceNumberCount',
      titleKey: 'dashboard.service-number-count',
      icon: 'customer-service',
      iconClass: 'service-icon',
      testIdSuffix: 'service'
    },
    { key: 'aiffCount', titleKey: 'dashboard.aiff-app-count', icon: 'appstore', iconClass: 'app-icon', testIdSuffix: 'aiff' },
    {
      key: 'onlineUserCount',
      titleKey: 'dashboard.online-user-count',
      icon: 'line-chart',
      iconClass: 'online-icon',
      testIdSuffix: 'online'
    },
    {
      key: 'thirtyDayActiveUserCount',
      titleKey: 'dashboard.thirty-day-active-user-count',
      icon: 'thunderbolt',
      iconClass: 'active-icon',
      testIdSuffix: 'active'
    }
  ];
  statistics: DashboardStatistics = this.createEmptyStatistics();

  ngOnInit(): void {
    this.currentTenantId = this.tenantStateService.getCurrentTenant() ?? '';
    if (!this.currentTenantId) {
      this.loading = false;
      this.hasError = true;
      return;
    }
    this.loadDashboardInfo();
  }

  reloadDashboard(): void {
    if (this.currentTenantId) {
      this.loadDashboardInfo();
    }
  }

  getStatisticValue(key: DashboardStatisticKey): number {
    return this.statistics[key];
  }

  private loadDashboardInfo(): void {
    this.loading = true;
    this.hasError = false;
    this.http
      .post(`${ConstantUtil.ADMIN_TENANT_DASHBOARD_INFO}/${this.currentTenantId}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (res.success && res.data) {
            this.statistics = this.mapStatistics(res.data);
          } else {
            this.hasError = true;
            this.statistics = this.createEmptyStatistics();
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.hasError = true;
          this.statistics = this.createEmptyStatistics();
          this.cdr.detectChanges();
        }
      });
  }

  private mapStatistics(data: Record<string, unknown>): DashboardStatistics {
    return {
      aiffCount: this.toCount(data['aiffCount']),
      employeeCount: this.toCount(data['employeeCount']),
      serviceNumberCount: this.toCount(data['serviceNumberCount']),
      bossServiceNumberCount: this.toCount(data['bossServiceNumberCount']),
      contactCount: this.toCount(data['contactCount']),
      onlineUserCount: this.toCount(data['onlineUserCount']),
      thirtyDayActiveUserCount: this.toCount(
        data['thirtyDayActiveUserCount'] ?? data['activeUserCount'] ?? data['recentThirtyDayActiveUserCount']
      )
    };
  }

  private createEmptyStatistics(): DashboardStatistics {
    return {
      aiffCount: 0,
      employeeCount: 0,
      serviceNumberCount: 0,
      bossServiceNumberCount: 0,
      contactCount: 0,
      onlineUserCount: 0,
      thirtyDayActiveUserCount: 0
    };
  }

  private toCount(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }
}
